import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Calculator,
  TrendingUp,
  Building2,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Info,
  ArrowRight,
  Shield,
  Landmark
} from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { wizardApi } from '../services/api/wizard'
import { Label } from '../components/ui/Label'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useHubData } from '../hooks/useHubData'
import { useAuth } from '../contexts/AuthContext'
import { useExchange } from '../contexts/ExchangeContext'
import {
  calculateExchange,
  formatCurrency,
  formatPercent,
  type ExchangeCalculationInput,
  type ExchangeCalculationResult,
} from '../utils/exchangeCalculations'

const calculatorSchema = z.object({
  originalCost: z.number().min(0, 'Must be positive'),
  capitalImprovements: z.number().min(0, 'Must be positive'),
  accumulatedDepreciation: z.number().min(0, 'Must be positive'),
  sellingPrice: z.number().min(0, 'Must be positive'),
  sellingExpenses: z.number().min(0, 'Must be positive'),
  replacementPropertyCost: z.number().min(0, 'Must be positive'),
  oldMortgagePayoff: z.number().min(0, 'Must be positive'),
  newMortgageAmount: z.number().min(0, 'Must be positive'),
  cashPaidAtClosing: z.number().min(0, 'Must be positive'),
  cashReceivedAtClosing: z.number().min(0, 'Must be positive'),
  filingStatus: z.enum(['single', 'married_joint', 'married_separate', 'head_of_household']),
  estimatedMAGI: z.number().min(0, 'Must be positive'),
  ltcgRate: z.number().refine(v => v === 15 || v === 20, 'Must be 15 or 20'),
  stateRate: z.number().min(0).max(20, 'Max 20%'),
})

type CalculatorFormData = z.infer<typeof calculatorSchema>

const FILING_STATUS_LABELS: Record<string, string> = {
  single: 'Single',
  married_joint: 'Married Filing Jointly',
  married_separate: 'Married Filing Separately',
  head_of_household: 'Head of Household',
}

const TAX_CHART_COLORS = ['#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6']

export const CalculatorPage: React.FC = () => {
  const { user } = useAuth()
  const { currentExchange } = useExchange()
  const salesData = useHubData('initial')
  const purchaseData = useHubData('acquisition')
  const [results, setResults] = useState<ExchangeCalculationResult | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [activeSection, setActiveSection] = useState<'property' | 'mortgage' | 'tax'>('property')

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<CalculatorFormData>({
    resolver: zodResolver(calculatorSchema),
    defaultValues: {
      originalCost: 500000,
      capitalImprovements: 50000,
      accumulatedDepreciation: 100000,
      sellingPrice: 800000,
      sellingExpenses: 40000,
      replacementPropertyCost: 900000,
      oldMortgagePayoff: 300000,
      newMortgageAmount: 450000,
      cashPaidAtClosing: 0,
      cashReceivedAtClosing: 0,
      filingStatus: 'married_joint',
      estimatedMAGI: 350000,
      ltcgRate: 20,
      stateRate: 9.3, // California
    }
  })

  // Populate from hub data if available
  useEffect(() => {
    if (!salesData.data && !purchaseData.data) return
    const disbursementAmount = purchaseData.data?.disbursementForm?.amount
    if (disbursementAmount) {
      const amount = parseFloat(disbursementAmount.replace(/[^0-9.-]/g, ''))
      if (!isNaN(amount) && amount > 0) {
        setValue('replacementPropertyCost', amount)
      }
    }
  }, [salesData.data, purchaseData.data, setValue])

  const calculatorStorageKey = user?.id
    ? `open1031:calculator:${user.id}`
    : null

  useEffect(() => {
    if (!calculatorStorageKey) return
    const raw = localStorage.getItem(calculatorStorageKey)
    if (!raw) return
    try {
      const stored = JSON.parse(raw) as { taxSavingsCents: number; calculatedAt: string; inputs: CalculatorFormData; results?: ExchangeCalculationResult }
      if (stored.inputs) {
        reset(stored.inputs)
      }
      if (stored.results) {
        setResults(stored.results)
      }
    } catch {}
  }, [calculatorStorageKey, reset])

  const onSubmit = async (data: CalculatorFormData) => {
    setIsCalculating(true)
    setTimeout(() => {
      const input: ExchangeCalculationInput = {
        ...data,
        ltcgRate: data.ltcgRate as 15 | 20,
        filingStatus: data.filingStatus as ExchangeCalculationInput['filingStatus'],
      }
      const calculated = calculateExchange(input)
      setResults(calculated)
      setIsCalculating(false)

      if (calculatorStorageKey) {
        localStorage.setItem(calculatorStorageKey, JSON.stringify({
          taxSavingsCents: Math.round(calculated.taxSavings * 100),
          calculatedAt: new Date().toISOString(),
          inputs: data,
          results: calculated,
        }))
        window.dispatchEvent(new CustomEvent('calculatorResultPersisted'))
      }
      localStorage.setItem('calculator_results', JSON.stringify({
        input: data,
        results: calculated,
        calculatedAt: new Date().toISOString(),
      }))
      localStorage.setItem('calculator_savings', JSON.stringify({
        savings: formatCurrency(calculated.taxSavings),
      }))
      if (currentExchange?.id) {
        wizardApi.saveCalculatorResults(currentExchange.id, {
          sellingPrice: data.sellingPrice,
          adjustedBasis: calculated.adjustedBasis,
          replacementCost: data.replacementPropertyCost,
          netCashBoot: calculated.boot?.netCash,
          totalBoot: calculated.boot?.totalBoot,
          recognizedGain: calculated.recognizedGain,
          deferredGain: calculated.deferredGain,
          basisInReplacement: calculated.replacementPropertyBasis,
          input: data as unknown as Record<string, unknown>,
          results: calculated as unknown as Record<string, unknown>,
        }).catch((err) => {
          console.warn('[CalculatorPage] failed to persist calculator to DB; localStorage cache retained:', err)
        })
      }
    }, 600)
  }

  const resetForm = () => {
    reset()
    setResults(null)
    if (calculatorStorageKey) {
      localStorage.removeItem(calculatorStorageKey)
    }
    localStorage.removeItem('calculator_results')
    localStorage.removeItem('calculator_savings')
  }

  // Tax breakdown chart data
  const taxChartData = results ? [
    { name: 'Depreciation Recapture (25%)', value: results.taxWithout1031.depreciationRecapture, color: TAX_CHART_COLORS[0] },
    { name: `LTCG (${results.taxWithout1031.ltcgRate * 100}%)`, value: results.taxWithout1031.ltcgTax, color: TAX_CHART_COLORS[1] },
    { name: 'NIIT (3.8%)', value: results.taxWithout1031.niitTax, color: TAX_CHART_COLORS[2] },
    { name: `State (${(results.taxWithout1031.stateRate * 100).toFixed(1)}%)`, value: results.taxWithout1031.stateTax, color: TAX_CHART_COLORS[3] },
  ].filter(d => d.value > 0) : []

  // Comparison bar data
  const comparisonData = results ? [
    { name: 'Without 1031', tax: results.taxWithout1031.totalTax, fill: '#ef4444' },
    { name: 'With 1031', tax: results.taxOn1031.totalTax, fill: '#22c55e' },
  ] : []

  const sectionTabs = [
    { key: 'property' as const, label: 'Property', icon: Building2 },
    { key: 'mortgage' as const, label: 'Mortgage & Cash', icon: Landmark },
    { key: 'tax' as const, label: 'Tax Profile', icon: Shield },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
            <Calculator className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            1031 Exchange Calculator
          </h1>
          <p className="text-muted-foreground mt-1">
            Full boot netting, multi-rate tax analysis, and exchange comparison
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={resetForm}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button className="dark:bg-primary-600 dark:hover:bg-primary-700">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Input Form — 2 columns */}
        <div className="lg:col-span-2 space-y-4">
          {/* Section Tabs */}
          <div className="flex rounded-lg bg-muted p-1 gap-1">
            {sectionTabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveSection(key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                  activeSection === key
                    ? 'bg-card text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Property Section */}
            {activeSection === 'property' && (
              <Card className="p-5 space-y-3">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary-500" />
                  Property Information
                </h2>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="originalCost" className="text-xs">Original Purchase Price</Label>
                    <Input id="originalCost" type="number" {...register('originalCost', { valueAsNumber: true })} />
                    {errors.originalCost && <p className="text-xs text-red-500 mt-0.5">{errors.originalCost.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="capitalImprovements" className="text-xs">Capital Improvements</Label>
                    <Input id="capitalImprovements" type="number" {...register('capitalImprovements', { valueAsNumber: true })} />
                  </div>
                  <div>
                    <Label htmlFor="accumulatedDepreciation" className="text-xs">Accumulated Depreciation</Label>
                    <Input id="accumulatedDepreciation" type="number" {...register('accumulatedDepreciation', { valueAsNumber: true })} />
                  </div>
                  <hr className="border-border" />
                  <div>
                    <Label htmlFor="sellingPrice" className="text-xs">Selling Price</Label>
                    <Input id="sellingPrice" type="number" {...register('sellingPrice', { valueAsNumber: true })} />
                  </div>
                  <div>
                    <Label htmlFor="sellingExpenses" className="text-xs">Selling Expenses (commissions, etc.)</Label>
                    <Input id="sellingExpenses" type="number" {...register('sellingExpenses', { valueAsNumber: true })} />
                  </div>
                  <hr className="border-border" />
                  <div>
                    <Label htmlFor="replacementPropertyCost" className="text-xs">Replacement Property Cost</Label>
                    <Input id="replacementPropertyCost" type="number" {...register('replacementPropertyCost', { valueAsNumber: true })} />
                  </div>
                </div>
              </Card>
            )}

            {/* Mortgage & Cash Section */}
            {activeSection === 'mortgage' && (
              <Card className="p-5 space-y-3">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Landmark className="h-4 w-4 text-primary-500" />
                  Mortgage & Cash
                </h2>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      Boot netting follows IRS asymmetric rules: excess cash paid can offset mortgage boot, but excess mortgage cannot offset cash boot.
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="oldMortgagePayoff" className="text-xs">Old Mortgage Payoff (Relinquished)</Label>
                    <Input id="oldMortgagePayoff" type="number" {...register('oldMortgagePayoff', { valueAsNumber: true })} />
                  </div>
                  <div>
                    <Label htmlFor="newMortgageAmount" className="text-xs">New Mortgage (Replacement)</Label>
                    <Input id="newMortgageAmount" type="number" {...register('newMortgageAmount', { valueAsNumber: true })} />
                  </div>
                  <hr className="border-border" />
                  <div>
                    <Label htmlFor="cashReceivedAtClosing" className="text-xs">Cash Received at Closing</Label>
                    <Input id="cashReceivedAtClosing" type="number" {...register('cashReceivedAtClosing', { valueAsNumber: true })} />
                  </div>
                  <div>
                    <Label htmlFor="cashPaidAtClosing" className="text-xs">Additional Cash Paid at Closing</Label>
                    <Input id="cashPaidAtClosing" type="number" {...register('cashPaidAtClosing', { valueAsNumber: true })} />
                  </div>
                </div>
              </Card>
            )}

            {/* Tax Profile Section */}
            {activeSection === 'tax' && (
              <Card className="p-5 space-y-3">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary-500" />
                  Tax Profile
                </h2>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="filingStatus" className="text-xs">Filing Status</Label>
                    <select
                      id="filingStatus"
                      {...register('filingStatus')}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {Object.entries(FILING_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="estimatedMAGI" className="text-xs">Estimated MAGI (for NIIT threshold)</Label>
                    <Input id="estimatedMAGI" type="number" {...register('estimatedMAGI', { valueAsNumber: true })} />
                  </div>
                  <div>
                    <Label htmlFor="ltcgRate" className="text-xs">Federal LTCG Rate</Label>
                    <select
                      id="ltcgRate"
                      {...register('ltcgRate', { valueAsNumber: true })}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={15}>15%</option>
                      <option value={20}>20% (income over $518K single / $583K joint)</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="stateRate" className="text-xs">State Tax Rate (%)</Label>
                    <Input id="stateRate" type="number" step="0.1" {...register('stateRate', { valueAsNumber: true })} />
                    <p className="text-[10px] text-muted-foreground mt-0.5">CA: 9.3% | NY: 8.82% | TX/FL/NV: 0%</p>
                  </div>
                </div>
              </Card>
            )}

            <Button
              type="submit"
              className="w-full mt-4"
              disabled={isCalculating}
            >
              {isCalculating ? (
                <>
                  <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate Exchange
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Results — 3 columns */}
        <div className="lg:col-span-3 space-y-4">
          {results ? (
            <>
              {/* Key Savings Banner */}
              <Card className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Estimated Tax Savings with 1031</p>
                    <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">
                      {formatCurrency(results.taxSavings)}
                    </p>
                  </div>
                  <div className="h-14 w-14 rounded-full bg-emerald-100 dark:bg-emerald-800/50 flex items-center justify-center">
                    <TrendingUp className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-emerald-600 dark:text-emerald-400">
                  <span>Deferred Gain: <strong>{formatCurrency(results.deferredGain)}</strong></span>
                  <span>New Basis: <strong>{formatCurrency(results.replacementPropertyBasis)}</strong></span>
                </div>
              </Card>

              {/* Boot Analysis */}
              <Card className="p-5">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  {results.boot.totalBoot > 0 ? (
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  Boot Analysis (IRS Netting)
                </h3>
                {results.boot.totalBoot > 0 ? (
                  <div className="space-y-2">
                    {/* Cash Boot */}
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50">
                      <div>
                        <p className="text-xs font-medium text-foreground">Net Cash</p>
                        <p className="text-[10px] text-muted-foreground">Received {formatCurrency(watch('cashReceivedAtClosing'))} - Paid {formatCurrency(watch('cashPaidAtClosing'))}</p>
                      </div>
                      <span className={`text-sm font-semibold ${results.boot.cashBoot > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                        {results.boot.cashBoot > 0 ? `${formatCurrency(results.boot.cashBoot)} boot` : formatCurrency(results.boot.netCash)}
                      </span>
                    </div>
                    {/* Excess Cash Paid offset */}
                    {results.boot.excessCashPaid > 0 && (
                      <div className="flex items-center justify-between p-2.5 rounded-lg bg-green-50 dark:bg-green-900/20">
                        <div>
                          <p className="text-xs font-medium text-green-700 dark:text-green-300">Excess Cash Paid (offsets mortgage boot)</p>
                        </div>
                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                          -{formatCurrency(results.boot.excessCashPaid)}
                        </span>
                      </div>
                    )}
                    {/* Mortgage Boot */}
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50">
                      <div>
                        <p className="text-xs font-medium text-foreground">Net Mortgage Relief</p>
                        <p className="text-[10px] text-muted-foreground">Old {formatCurrency(watch('oldMortgagePayoff'))} - New {formatCurrency(watch('newMortgageAmount'))}</p>
                      </div>
                      <span className={`text-sm font-semibold ${results.boot.mortgageBoot > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                        {results.boot.mortgageBoot > 0 ? `${formatCurrency(results.boot.mortgageBoot)} relief` : formatCurrency(results.boot.netMortgageRelief)}
                      </span>
                    </div>
                    {/* Taxable Mortgage Boot (after offset) */}
                    {results.boot.mortgageBoot > 0 && results.boot.mortgageBoot !== results.boot.taxableMortgageBoot && (
                      <div className="flex items-center justify-between p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                        <div>
                          <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Taxable Mortgage Boot (after offset)</p>
                        </div>
                        <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                          {formatCurrency(results.boot.taxableMortgageBoot)}
                        </span>
                      </div>
                    )}
                    {/* Total */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                      <p className="text-sm font-semibold text-red-700 dark:text-red-300">Total Taxable Boot</p>
                      <span className="text-lg font-bold text-red-600 dark:text-red-400">{formatCurrency(results.boot.totalBoot)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-center">
                    <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">No Boot Received</p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">Full gain deferral — no taxable boot in this exchange.</p>
                  </div>
                )}
              </Card>

              {/* Tax Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Without 1031 vs With 1031 */}
                <Card className="p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Tax Comparison</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={comparisonData} layout="vertical" margin={{ left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(value: number) => [formatCurrency(value), 'Tax']} />
                        <Bar dataKey="tax" radius={[0, 4, 4, 0]} barSize={28}>
                          {comparisonData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 flex items-center justify-center gap-2 text-xs">
                    <span className="text-red-500 font-medium">{formatCurrency(results.taxWithout1031.totalTax)}</span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className="text-green-500 font-medium">{formatCurrency(results.taxOn1031.totalTax)}</span>
                    <span className="text-muted-foreground">
                      ({formatPercent(results.taxWithout1031.effectiveRate)} eff. rate)
                    </span>
                  </div>
                </Card>

                {/* Tax Breakdown Donut */}
                <Card className="p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Tax Breakdown (Without 1031)</h3>
                  {taxChartData.length > 0 ? (
                    <>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={taxChartData}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={70}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {taxChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="space-y-1.5 mt-1">
                        {taxChartData.map((item) => (
                          <div key={item.name} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                              <span className="text-muted-foreground">{item.name}</span>
                            </div>
                            <span className="font-medium text-foreground">{formatCurrency(item.value)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-8">No taxable gain</p>
                  )}
                </Card>
              </div>

              {/* Detailed Breakdown */}
              <Card className="p-5">
                <h3 className="text-sm font-semibold text-foreground mb-3">Detailed Exchange Summary</h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Adjusted Basis</span>
                    <span className="font-medium text-foreground">{formatCurrency(results.adjustedBasis)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Realized Gain</span>
                    <span className="font-medium text-foreground">{formatCurrency(results.realizedGain)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Recognized Gain (taxable)</span>
                    <span className={`font-medium ${results.recognizedGain > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {formatCurrency(results.recognizedGain)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Deferred Gain</span>
                    <span className="font-medium text-green-600 dark:text-green-400">{formatCurrency(results.deferredGain)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Boot</span>
                    <span className={`font-medium ${results.boot.totalBoot > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                      {formatCurrency(results.boot.totalBoot)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Replacement Property Basis</span>
                    <span className="font-medium text-foreground">{formatCurrency(results.replacementPropertyBasis)}</span>
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <Card className="p-8">
              <div className="text-center py-12">
                <div className="h-16 w-16 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-4">
                  <Calculator className="h-8 w-8 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Ready to Calculate
                </h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  Enter your property, mortgage, and tax details to see a full 1031 exchange analysis with boot netting and multi-rate tax breakdown.
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Tips */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-foreground mb-3">How Boot Netting Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-muted-foreground">
          <div>
            <h3 className="font-medium text-foreground mb-1">Cash Boot</h3>
            <p>Cash received at closing minus cash paid. If you receive net cash, it's taxable boot.</p>
          </div>
          <div>
            <h3 className="font-medium text-foreground mb-1">Mortgage Boot</h3>
            <p>Old mortgage paid off minus new mortgage. If your debt decreases, the relief is treated like receiving cash. Excess cash paid CAN offset this.</p>
          </div>
          <div>
            <h3 className="font-medium text-foreground mb-1">IRS Asymmetric Rule</h3>
            <p>Excess cash paid offsets mortgage boot, but excess mortgage does NOT offset cash boot. This is the most commonly misunderstood rule.</p>
          </div>
        </div>
      </Card>
    </div>
  )
}
